/**
 * Period-scoped payroll readiness aggregation.
 *
 * Pure functions over assignment/timesheet/reconciliation rows — no I/O.
 * Server loaders in apps/web query Supabase then call these aggregators.
 */

export type PayrollCalculationStatus = 'READY' | 'BLOCKED' | 'UNKNOWN';
export type PayrollExportStatus = 'READY' | 'BLOCKED' | 'NOT_REQUIRED';

export type PeriodReadinessBlockerCode =
  | 'ASSIGNMENT_PAY_CONTEXT_BLOCKED'
  | 'ASSIGNMENT_PAY_CONTEXT_NOT_RESOLVED'
  | 'PAY_IDENTITY_CORRECTION_PENDING'
  | 'ASSIGNMENT_SETUP_CHANGED_AFTER_APPROVAL'
  | 'ASSIGNMENT_OFFER_NOT_RESOLVED'
  | 'ASSIGNMENT_NO_TIMESHEET'
  | 'ASSIGNMENT_COVERED_BY_APPROVED_LEAVE'
  | 'TIMESHEET_ZERO_PAY'
  | 'TIMESHEET_PAY_CONTEXT_NOT_RESOLVED'
  | 'TIMESHEET_PAY_CONTEXT_STALE'
  | 'TIMESHEET_REJECTED'
  | 'TIMESHEET_AND_APPROVED_LEAVE_SAME_DAY'
  | 'TIMESHEET_NOT_APPROVED'
  | 'RATE_SCHEDULE_GAP'
  | 'WORKER_MISSING_PAY_PROFILE'
  | 'LEAVE_ROSTER_EVIDENCE_GAP'
  | 'EBA_EXPORT_NOT_READY'
  /** MZ-004: worker base unreadable in a multi-timezone tenant; the timesheet is held, not the run. */
  | 'PAY_CLOCK_BASE_CALENDAR_UNAVAILABLE'
  /** MZ-004 F4: a recorded averaging cycle differs from the published roster period (advisory). */
  | 'AVERAGING_CYCLE_DIFFERS_FROM_ROSTER'
  /** MZ-004 cl 29.2(a)(ii): elected public holiday leave hours cannot reach Xero (advisory). */
  | 'PUBLIC_HOLIDAY_LEAVE_CREDIT_NOT_POSTABLE';

export interface PeriodReadinessBlocker {
  code: PeriodReadinessBlockerCode | string;
  /** Accountable party for the money hold, independent of care/time capture ownership. */
  owner?: 'PAYROLL' | 'HELD_BY_STATURA';
  severity: 'HARD' | 'SOFT';
  message: string;
  resourceType?:
    | 'roster_assignment'
    | 'timesheet'
    | 'staff'
    | 'eba_agreement'
    | 'rate_line'
    | 'leave_request';
  resourceId?: string;
  fixHref?: string;
  /** Who and when, for the operator's worklist (for example "Donna Gray · 2026-09-04"). */
  subject?: string;
  /** The shift is dated after the tenant's today: nothing can be worked or timesheeted yet. */
  notYetWorked?: boolean;
}

/** Joins a worker name and a date for a blocker's subject column; undefined when neither is known. */
export function blockerSubject(
  staffName?: string | null,
  date?: string | null,
): string | undefined {
  const parts = [staffName?.trim(), date?.trim()].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

export interface PayrollExportReadinessInput {
  status: PayrollExportStatus;
  targetSystem: 'XERO' | 'CSV' | 'MYOB' | 'KEYPAY' | 'MANUAL' | 'OTHER' | 'NONE';
  ebaId: string | null;
  blockers: Array<{ code: string; message: string; route?: string }>;
}

export interface PayrollPeriodAssignmentInput {
  id: string;
  shiftId?: string | null;
  slotId?: string | null;
  staffId: string | null;
  isAgency: boolean;
  assignmentStatus: string;
  payContextStatus: string;
  payContextBlockers: Array<{ code: string; message: string; severity?: string; source?: string }>;
  assignmentSource?: string | null;
  payEventFactSetId?: string | null;
  plannedWorkingUpSnapshot?: Record<string, unknown> | null;
  /** Pricing-held fatigue evidence remains mandatory after profile repair. */
  deferredPayFactCapture?: boolean;
  /** Worker display name, when the loader resolved it; used only for blocker subjects. */
  staffName?: string | null;
  /** Shift date (tenant-local YYYY-MM-DD), when the loader resolved it. */
  shiftDate?: string | null;
}

export interface PayrollPeriodTimesheetInput {
  id: string;
  staffId: string;
  rosterAssignmentId: string | null;
  staffName?: string | null;
  /** Timesheet date (tenant-local YYYY-MM-DD), when the loader resolved it. */
  date?: string | null;
  paymentMethod?: string | null;
  isSalaryFlat?: boolean | null;
  payContextStatus: string | null;
  payContextSnapshot: Record<string, unknown> | null;
  totalHours: number | string | null;
  totalPay: number | string | null;
  status: string;
  payRunId: string | null;
  payIdentityCorrectionPending?: boolean | null;
  /** Private DB lineage projection; portable source-evidence markers are not authority. */
  terminalCloseoutLineageVerified?: boolean | null;
  /** Raw capture evidence used only for exact, controlled terminal-source disposition checks. */
  sourceEvidence?: Record<string, unknown> | null;
  /** Current status of the live linked assignment; never trusted from source evidence. */
  linkedAssignmentStatus?: string | null;
  /** True only when an approved leave request covers this timesheet's tenant-local date. */
  coveredByApprovedLeave?: boolean | null;
  /** Authoritative, segment-backed leave hours for this worker's tenant-local date. */
  approvedLeaveHoursOnDate?: number | null;
  /** All live rostered paid hours for this worker's tenant-local date, when known. */
  scheduledPaidHoursOnDate?: number | null;
  /** A paid leave segment identifies this exact roster assignment, never merely its date. */
  identifiedPaidLeaveHoursForAssignment?: number | null;
  /** Gross start-to-end window for that exact assignment; breaks are deliberately not deducted. */
  grossScheduledWindowHoursForAssignment?: number | null;
  /** True only when the linked leave segment is one this run's snapshot will pay. */
  identifiedPaidLeaveWillBePaidInRun?: boolean | null;
}

export interface PayrollPeriodReconciliationInput {
  rosterAssignmentId: string;
  staffId: string | null;
  timesheetId: string | null;
  assignmentStatus: string;
  payContextStatus?: string | null;
  timesheetZeroPay?: boolean | null;
  staffName?: string | null;
  shiftDate?: string | null;
  /**
   * True only when the pay-run snapshotter will synthesise this worker's
   * salary for the whole period: one SALARY_FLAT staff_payroll_assignment
   * covers every day of it (the getSalaryFlatStaff selection). Their pay never
   * reads a timesheet, so a roster assignment without one is not a payroll
   * gap. Anything else, including a mid-period change to or from salary and a
   * failed lookup, leaves it unset and keeps the blocker: fail closed.
   */
  salaryFlatPaidForPeriod?: boolean | null;
  /**
   * True only when a tenant-scoped APPROVED leave request for this worker
   * covers this rostered shift's tenant-local date. The leave, rather than a
   * timesheet, is paid. A failed or incomplete leave lookup leaves this unset
   * and keeps the missing-timesheet blocker: fail closed.
   */
  coveredByApprovedLeave?: boolean | null;
}

/**
 * Whether a roster assignment with no timesheet can be ignored for payroll.
 *
 * Only workers the snapshotter pays by salary for the whole period, or whose
 * exact assignment is identified by approved leave, qualify.
 * Medea's salaried A/CEO and Clinical Manager are rostered for presence and
 * excluded from timesheet presumption (14 Sep 2026); without this, each of
 * their rostered days became a HARD ASSIGNMENT_NO_TIMESHEET blocker and the
 * pay run refused to snapshot. Award workers keep the blocker, because their
 * pay depends on a timesheet. So does a worker who moves from award to salary
 * mid-period: the snapshotter pays that period from timesheets.
 */
export function reconciliationRowNeedsNoTimesheet(row: PayrollPeriodReconciliationInput): boolean {
  return row.salaryFlatPaidForPeriod === true || row.coveredByApprovedLeave === true;
}

export interface RateScheduleKey {
  classificationStream: string;
  classificationLevel: string;
  payPoint: string | null;
  awardRateVersionId: string;
}

export interface RateScheduleLine {
  classificationStream: string;
  classificationLevel: string;
  payPoint: string | null;
  awardRateVersionId: string;
}

export interface PayrollPeriodReadinessMetrics {
  assignmentCount: number;
  assignmentsResolved: number;
  assignmentsBlocked: number;
  assignmentsWithoutTimesheet: number;
  timesheetCount: number;
  timesheetsZeroPay: number;
  timesheetsUnresolvedContext: number;
  timesheetsPendingApproval: number;
  workersMissingPayProfile: number;
  rateScheduleGaps: number;
  totalGrossPay: number;
}

export type LeavePeriodEvidencePackProjection = {
  contentHashSha256: string | null;
  storagePath: string | null;
  csvStoragePath?: string | null;
  generatedAt: string | null;
};

/** Same shape as the leave projection: the latest TIMESHEET_CORRECTIONS_EVIDENCE_PACK run. */
export type TimesheetCorrectionsEvidencePackProjection = LeavePeriodEvidencePackProjection;

export interface PayrollPeriodReadinessSummary {
  rosterPeriodId: string;
  periodStart: string | null;
  periodEnd: string | null;
  calculation: {
    status: PayrollCalculationStatus;
    blockers: PeriodReadinessBlocker[];
    metrics: PayrollPeriodReadinessMetrics;
    /** Active source exclusions for the selected draft; metrics remain period-wide. */
    heldOutSourceCount?: number;
  };
  export: PayrollExportReadinessInput;
  /** Latest LEAVE_PERIOD_EVIDENCE_PACK run for this roster period, when present. */
  leavePeriodEvidence?: LeavePeriodEvidencePackProjection | null;
  /** Latest TIMESHEET_CORRECTIONS_EVIDENCE_PACK run for this roster period, when present. */
  timesheetCorrectionsEvidence?: TimesheetCorrectionsEvidencePackProjection | null;
}

const PAY_ROLE_FIX_HREF_CODES = new Set([
  'PAY_ROLE_AMBIGUOUS',
  'PAY_ROLE_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_REQUIRED',
  'PAY_ROLE_ASSIGNMENT_STALE',
  'PAY_ROLE_NOT_FOUND',
  'PAY_ROLE_NOT_ACTIVE',
  'PAY_ROLE_ELIGIBILITY_BLOCKED',
]);

/** Deep-link for assignment/timesheet pay-context blockers surfaced in the readiness hub. */
export function resolvePayContextBlockerFixHref(input: {
  staffId: string | null;
  nestedBlockerCode?: string | null;
}): string | undefined {
  if (!input.staffId) return undefined;
  const code = input.nestedBlockerCode?.trim();
  if (code && PAY_ROLE_FIX_HREF_CODES.has(code)) {
    return `/settings/payroll/pay-roles?staff_id=${input.staffId}`;
  }
  return `/people/workers/${input.staffId}/pay-profile`;
}

/**
 * PAY_ROLE_REQUIRED officer copy. A default pay role never decides between
 * candidate roles, so this copy must never send the officer to "Set as default".
 */
export const PAY_ROLE_REQUIRED_MESSAGE =
  'This worker holds more than one pay role that could fit this shift, and none matches the shift’s department exactly. Choose the role for this shift if you are offered one, set the Roster service department on the worker’s pay role so it matches the shift, or use Suspend assignment on the worker’s Pay profile for a role they no longer hold.';

export const PAY_CONTEXT_BLOCKER_LABELS: Record<string, string> = {
  PAY_ROLE_REQUIRED: PAY_ROLE_REQUIRED_MESSAGE,
  PAY_ROLE_ASSIGNMENT_REQUIRED:
    'Selected pay role is not assigned to this worker for the shift date.',
  PAY_ROLE_ASSIGNMENT_STALE:
    'Roster assignment pins a pay role that is not assigned to this worker for the shift date.',
  PAY_ROLE_NOT_FOUND: 'Pay role record was not found.',
  PAY_ROLE_NOT_ACTIVE: 'Pay role is not active.',
  PAY_ROLE_ELIGIBILITY_BLOCKED: 'Worker pay-role assignment is not eligible.',
  PAY_ROLE_AMBIGUOUS: 'Multiple pay roles match — explicit selection required.',
  STAFF_PAY_PROFILE_NOT_FOUND: 'No pay profile covers this worker for the shift date.',
  STAFF_PAY_PROFILE_AMBIGUOUS: 'Multiple active pay profiles cover this worker for the shift date.',
  STAFF_CLASSIFICATION_NOT_FOUND:
    'No classification history covers this worker for the shift date.',
  RATE_NOT_FOUND: 'No rate line matches the worker classification in the active rate schedule.',
  PAY_FACTS_MISSING: 'Required pay facts are missing for this event.',
  ALLOWANCE_PAY_CERTIFICATION_REQUIRED:
    'Payroll must recertify allowance pay before this timesheet can enter a pay run.',
};

const RESOLVED_PAY_CONTEXT = new Set(['RESOLVED']);
/**
 * Legacy control id from the 13–26 July 2026 Medea/Inerva one-off closeout.
 * Keep the constant name and value: live markers and audit evidence already
 * carry it. Do not rename.
 */
export const MEDEA_TERMINAL_SOURCE_CLOSEOUT_CONTROL_ID =
  '20270630000004_medea_terminal_source_closeout';
/**
 * Audited terminal non-payable closeout for Medea FE 27 Jul – 9 Aug 2026.
 * Second period to need the disposition; accepted alongside the legacy id.
 */
export const MEDEA_FE_27JUL_09AUG_REJECTED_TERMINAL_CLOSEOUT_CONTROL_ID =
  '20270958000000_medea_fe_27jul_09aug_rejected_terminal_closeout';
/**
 * Historical closeout control ids used by source-evidence writers and audits.
 * Readiness itself consumes the private row-lineage projection; portable
 * control ids are not authority.
 */
export const AUDITED_TERMINAL_NON_PAYABLE_CLOSEOUT_CONTROL_IDS = new Set([
  MEDEA_TERMINAL_SOURCE_CLOSEOUT_CONTROL_ID,
  MEDEA_FE_27JUL_09AUG_REJECTED_TERMINAL_CLOSEOUT_CONTROL_ID,
]);
export const PAYROLL_RELEVANT_ASSIGNMENT_STATUSES = new Set([
  'OFFERED',
  'ASSIGNED',
  'ACCEPTED',
  'IN_PROGRESS',
  'COMPLETED',
]);
const ASSIGNMENT_PAY_FACT_BLOCKER_CODES = new Set([
  'PAY_FACT_SET_REQUIRED',
  'PAY_FACT_SET_NOT_APPROVED',
  'PAY_FACTS_MISSING',
]);

/** Period context required for portable audited terminal non-payable recognition. */
export type AuditedTerminalCloseoutPeriod = {
  rosterPeriodId: string;
  periodStart?: string | null;
  periodEnd?: string | null;
};

function asNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function payContextSchemaVersion(
  snapshot: Record<string, unknown> | null | undefined,
): number | null {
  const version = snapshot?.schemaVersion;
  return typeof version === 'number' ? version : null;
}

function payContextText(
  snapshot: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = snapshot?.[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

function normalisePayContextStatus(status: string | null | undefined): string {
  return String(status ?? 'NOT_RESOLVED')
    .trim()
    .toUpperCase();
}

function isStaffedAssignment(row: PayrollPeriodAssignmentInput): boolean {
  return Boolean(row.staffId) && !row.isAgency;
}

export function isPayrollRelevantAssignmentStatus(status: string | null | undefined): boolean {
  return PAYROLL_RELEVANT_ASSIGNMENT_STATUSES.has(
    String(status ?? '')
      .trim()
      .toUpperCase(),
  );
}

/**
 * Fail-closed disposition contract for audited terminal non-payable rejected
 * timesheets. The private database lineage projection is the authority; the
 * portable marker and mutable assignment row are deliberately not re-used as
 * identity. A copied marker therefore remains a blocker, while later marker
 * corruption cannot make a ledgered terminal row appear recoverable.
 */
export function isAuditedTerminalNonPayableRejectedTimesheet(
  timesheet: Pick<
    PayrollPeriodTimesheetInput,
    | 'status'
    | 'payRunId'
    | 'rosterAssignmentId'
    | 'sourceEvidence'
    | 'linkedAssignmentStatus'
    | 'terminalCloseoutLineageVerified'
  >,
  _period: AuditedTerminalCloseoutPeriod | string,
): boolean {
  if (
    String(timesheet.status ?? '')
      .trim()
      .toUpperCase() !== 'REJECTED'
  )
    return false;
  // The private append-only ledger row is the authority. A roster assignment is
  // NOT required: six of Medea's 10-23 Aug rejections were manual rows with no
  // assignment, and requiring one made them permanently unclearable. The ledger
  // can only be written by a migration or the controlled closeout RPC, so
  // dropping the assignment requirement does not widen who can clear a blocker.
  if (timesheet.payRunId !== null) return false;
  return timesheet.terminalCloseoutLineageVerified === true;
}

/**
 * Assignment fact sets are event-scoped evidence. A generic EA rule-fact list
 * does not make every ordinary roster assignment require its own fact set.
 */
export function assignmentRequiresEventPayFactSet(
  row: Pick<
    PayrollPeriodAssignmentInput,
    'assignmentSource' | 'payEventFactSetId' | 'plannedWorkingUpSnapshot'
  >,
): boolean {
  const workingUp = row.plannedWorkingUpSnapshot;
  return (
    String(row.assignmentSource ?? '').toUpperCase() === 'EXCEPTION_APPROVED' ||
    String(workingUp?.kind ?? '').toUpperCase() === 'WORKING_UP' ||
    Boolean(row.payEventFactSetId)
  );
}

/**
 * Whether an assignment's event fact set can hold a pay run.
 *
 * Only a working-up (higher duties) fact set changes what a worker is paid: it
 * moves the rate. A roster-exception fact set records that a coordinator
 * overrode a roster warning (short break, weekly hours) and carries nothing the
 * pricing path reads; the snapshot consumes fact VALUES only from timesheet,
 * on-call and recall evidence. The coordinator's override is already an audited
 * control at the time it is made, so re-approving it in payroll a fortnight
 * later protected nothing and, on Medea's 7-20 Sep 2026 run, held the snapshot
 * on 18 records behind a 218-item unfiltered review queue.
 *
 * `assignmentRequiresEventPayFactSet` is deliberately left as it was: the
 * pay-context replay still uses it to decide which assignments need a fact-set
 * pointer refreshed.
 */
export function assignmentFactSetGatesPayroll(
  row: Pick<PayrollPeriodAssignmentInput, 'plannedWorkingUpSnapshot' | 'deferredPayFactCapture'>,
): boolean {
  return (
    row.deferredPayFactCapture === true ||
    String(row.plannedWorkingUpSnapshot?.kind ?? '').toUpperCase() === 'WORKING_UP'
  );
}

function isAssignmentPayFactBlockerCode(code: unknown): boolean {
  return ASSIGNMENT_PAY_FACT_BLOCKER_CODES.has(String(code ?? '').trim());
}

/**
 * True when the ONLY thing keeping an assignment's pay context unresolved is
 * fact-set review that cannot change pay. Such an assignment is ready for
 * payroll. Any other blocker, an empty blocker list, or a working-up fact set
 * keeps the assignment blocked: fail closed.
 */
function assignmentHeldOnlyByNonGatingFactReview(row: PayrollPeriodAssignmentInput): boolean {
  if (assignmentFactSetGatesPayroll(row)) return false;
  const nested = row.payContextBlockers ?? [];
  if (nested.length === 0) return false;
  return nested.every((blocker) => isAssignmentPayFactBlockerCode(blocker.code));
}

function assignmentBlockingContextBlocker(
  row: PayrollPeriodAssignmentInput,
): PayrollPeriodAssignmentInput['payContextBlockers'][number] | undefined {
  return row.payContextBlockers?.find((blocker) => {
    if (isAssignmentPayFactBlockerCode(blocker.code)) {
      return assignmentFactSetGatesPayroll(row);
    }
    return blocker.severity === 'HARD';
  });
}

export function rateScheduleKeyMatchesLine(key: RateScheduleKey, line: RateScheduleLine): boolean {
  if (key.awardRateVersionId !== line.awardRateVersionId) return false;
  if (key.classificationStream !== line.classificationStream) return false;
  if (key.payPoint) {
    return key.payPoint === line.payPoint;
  }
  if (key.classificationLevel !== line.classificationLevel) return false;
  return line.payPoint === null || line.payPoint === key.classificationLevel;
}

export function scanRateScheduleGaps(
  keys: RateScheduleKey[],
  lines: RateScheduleLine[],
): RateScheduleKey[] {
  const uniqueKeys = new Map<string, RateScheduleKey>();
  for (const key of keys) {
    if (!key.awardRateVersionId || !key.classificationStream || !key.classificationLevel) continue;
    const mapKey = [
      key.awardRateVersionId,
      key.classificationStream,
      key.classificationLevel,
      key.payPoint ?? '',
    ].join('|');
    uniqueKeys.set(mapKey, key);
  }

  const gaps: RateScheduleKey[] = [];
  for (const key of uniqueKeys.values()) {
    const hasLine = lines.some((line) => rateScheduleKeyMatchesLine(key, line));
    if (!hasLine) gaps.push(key);
  }
  return gaps;
}

export function collectRateScheduleKeysFromSnapshots(
  timesheets: PayrollPeriodTimesheetInput[],
): RateScheduleKey[] {
  const keys: RateScheduleKey[] = [];
  for (const row of timesheets) {
    const snapshot = row.payContextSnapshot;
    if (!snapshot || payContextSchemaVersion(snapshot) !== 2) continue;
    const awardRateVersionId = payContextText(snapshot, 'awardRateVersionId');
    const stream =
      payContextText(snapshot, 'paidClassificationStream', 'classificationStream') ?? '';
    const level = payContextText(snapshot, 'paidClassificationLevel', 'classificationLevel') ?? '';
    const payPoint = payContextText(snapshot, 'paidPayPoint', 'payPoint');
    if (!awardRateVersionId || !stream || !level) continue;
    keys.push({
      awardRateVersionId,
      classificationStream: stream,
      classificationLevel: level,
      payPoint,
    });
  }
  return keys;
}

function isSalaryFlatTimesheet(timesheet: PayrollPeriodTimesheetInput): boolean {
  if (timesheet.isSalaryFlat === true) return true;
  if (
    String(timesheet.paymentMethod ?? '')
      .trim()
      .toUpperCase() === 'SALARY_FLAT'
  ) {
    return true;
  }
  const snapshot = timesheet.payContextSnapshot;
  return (
    payContextText(snapshot, 'baseAwardCode', 'base_award_code') === 'SALARY_FLAT' ||
    payContextText(snapshot, 'classificationStream', 'classification_stream') === 'SALARY_FLAT' ||
    payContextText(snapshot, 'paidClassificationStream', 'paid_classification_stream') ===
      'SALARY_FLAT'
  );
}

export interface AggregatePayrollPeriodReadinessInput {
  rosterPeriodId: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  /** Tenant-local today (YYYY-MM-DD); lets future-dated shifts be told apart from missing work. */
  tenantToday?: string | null;
  assignments: PayrollPeriodAssignmentInput[];
  timesheets: PayrollPeriodTimesheetInput[];
  reconciliation: PayrollPeriodReconciliationInput[];
  workersMissingPayProfileIds?: string[];
  rateScheduleLines?: RateScheduleLine[];
  exportReadiness?: PayrollExportReadinessInput;
  leavePeriodEvidence?: LeavePeriodEvidencePackProjection | null;
  timesheetCorrectionsEvidence?: TimesheetCorrectionsEvidencePackProjection | null;
}

export function aggregatePayrollPeriodReadiness(
  input: AggregatePayrollPeriodReadinessInput,
): PayrollPeriodReadinessSummary {
  const staffedAssignments = input.assignments.filter(
    (row) => isStaffedAssignment(row) && isPayrollRelevantAssignmentStatus(row.assignmentStatus),
  );
  const blockers: PeriodReadinessBlocker[] = [];
  const payrollRelevantTimesheets = input.timesheets.filter(
    (timesheet) =>
      !isAuditedTerminalNonPayableRejectedTimesheet(timesheet, {
        rosterPeriodId: input.rosterPeriodId,
        periodStart: input.periodStart,
        periodEnd: input.periodEnd,
      }),
  );

  let assignmentsResolved = 0;
  let assignmentsBlocked = 0;
  const approvedTimesheetByAssignmentId = new Map(
    payrollRelevantTimesheets
      .filter(
        (timesheet) =>
          ['APPROVED', 'PAID'].includes(
            String(timesheet.status ?? '')
              .trim()
              .toUpperCase(),
          ) && Boolean(timesheet.rosterAssignmentId),
      )
      .map((timesheet) => [timesheet.rosterAssignmentId as string, timesheet]),
  );

  for (const assignment of staffedAssignments) {
    const status = normalisePayContextStatus(assignment.payContextStatus);
    const blockingNested = assignmentBlockingContextBlocker(assignment);
    const approvedTimesheet = approvedTimesheetByAssignmentId.get(assignment.id);
    if (approvedTimesheet?.payIdentityCorrectionPending) {
      assignmentsBlocked += 1;
      blockers.push({
        code: 'PAY_IDENTITY_CORRECTION_PENDING',
        severity: 'HARD',
        message: 'An independently reviewed pay-identity correction is pending for this shift.',
        subject: blockerSubject(assignment.staffName, assignment.shiftDate),
        resourceType: 'roster_assignment',
        resourceId: assignment.id,
      });
      continue;
    }
    // A missing-profile override holds money even if captured time is approved.
    // Approved time does not certify pricing for this assignment.
    const profileHold = assignment.payContextBlockers?.find(
      (blocker) =>
        blocker.source === 'roster-exception-override/1' &&
        blocker.code === 'PAY_PROFILE_REQUIRED' &&
        blocker.severity === 'HARD',
    );
    if (profileHold) {
      assignmentsBlocked += 1;
      blockers.push({
        code: 'ASSIGNMENT_PAY_CONTEXT_BLOCKED',
        severity: 'HARD',
        message: profileHold.message,
        resourceType: 'roster_assignment',
        resourceId: assignment.id,
        subject: blockerSubject(assignment.staffName, assignment.shiftDate),
        fixHref: '/payroll/readiness',
      });
      continue;
    }
    if (assignment.deferredPayFactCapture && status !== 'RESOLVED') {
      assignmentsBlocked += 1;
      blockers.push({
        code: 'ASSIGNMENT_PAY_CONTEXT_BLOCKED',
        severity: 'HARD',
        message:
          'Deferred fatigue evidence must be captured and independently approved before payroll.',
        resourceType: 'roster_assignment',
        resourceId: assignment.id,
        fixHref: '/payroll/readiness',
        subject: blockerSubject(assignment.staffName, assignment.shiftDate),
      });
      continue;
    }
    if (approvedTimesheet) {
      assignmentsResolved += 1;
      if (
        !RESOLVED_PAY_CONTEXT.has(status) ||
        blockingNested ||
        String(assignment.assignmentStatus).trim().toUpperCase() === 'OFFERED'
      ) {
        blockers.push({
          code: 'ASSIGNMENT_SETUP_CHANGED_AFTER_APPROVAL',
          severity: 'SOFT',
          message:
            'Shift setup changed after timesheet approval. The approved timesheet pay context governs this shift; review any later setup change separately.',
          subject: blockerSubject(assignment.staffName, assignment.shiftDate),
          resourceType: 'roster_assignment',
          resourceId: assignment.id,
        });
      }
      continue;
    }
    if (String(assignment.assignmentStatus).trim().toUpperCase() === 'OFFERED') {
      assignmentsBlocked += 1;
      const shiftId = String(assignment.shiftId ?? '').trim();
      const slotId = String(assignment.slotId ?? '').trim();
      blockers.push({
        code: 'ASSIGNMENT_OFFER_NOT_RESOLVED',
        severity: 'HARD',
        message:
          'Outstanding roster offer must be accepted by the worker or resolved through the manager replacement flow before payroll.',
        subject: blockerSubject(assignment.staffName, assignment.shiftDate),
        resourceType: 'roster_assignment',
        resourceId: assignment.id,
        fixHref: shiftId
          ? `/roster/replace/${encodeURIComponent(shiftId)}?slot=${encodeURIComponent(slotId)}&assignment=${encodeURIComponent(assignment.id)}`
          : undefined,
      });
      continue;
    }
    if (RESOLVED_PAY_CONTEXT.has(status) && !blockingNested) {
      assignmentsResolved += 1;
      continue;
    }
    if (!blockingNested && assignmentHeldOnlyByNonGatingFactReview(assignment)) {
      assignmentsResolved += 1;
      continue;
    }

    assignmentsBlocked += 1;
    const nested = blockingNested ?? assignment.payContextBlockers?.[0];
    blockers.push({
      code:
        blockingNested || status === 'BLOCKED' || status === 'AMBIGUOUS'
          ? 'ASSIGNMENT_PAY_CONTEXT_BLOCKED'
          : 'ASSIGNMENT_PAY_CONTEXT_NOT_RESOLVED',
      severity: 'HARD',
      message:
        // Rows resolved before the copy fix persisted a "Set as default" remedy
        // that can never clear this blocker; always show the current copy.
        (nested?.code === 'PAY_ROLE_REQUIRED' ? PAY_ROLE_REQUIRED_MESSAGE : undefined) ??
        nested?.message ??
        PAY_CONTEXT_BLOCKER_LABELS[nested?.code ?? ''] ??
        `Assignment pay context is ${status}.`,
      subject: blockerSubject(assignment.staffName, assignment.shiftDate),
      resourceType: 'roster_assignment',
      resourceId: assignment.id,
      fixHref: resolvePayContextBlockerFixHref({
        staffId: assignment.staffId,
        nestedBlockerCode: nested?.code,
      }),
    });
  }

  const assignmentsWithoutTimesheet = input.reconciliation.filter(
    (row) =>
      row.staffId &&
      !row.timesheetId &&
      isPayrollRelevantAssignmentStatus(row.assignmentStatus) &&
      !reconciliationRowNeedsNoTimesheet(row),
  ).length;

  for (const row of input.reconciliation) {
    if (row.timesheetId || !row.staffId) continue;
    if (!isPayrollRelevantAssignmentStatus(row.assignmentStatus)) {
      continue;
    }
    if (reconciliationRowNeedsNoTimesheet(row)) {
      if (row.coveredByApprovedLeave === true) {
        blockers.push({
          code: 'ASSIGNMENT_COVERED_BY_APPROVED_LEAVE',
          severity: 'SOFT',
          message:
            'This rostered shift is covered by approved leave, so no timesheet is needed. The leave is paid instead. Release the shift from the roster to tidy it up.',
          subject: blockerSubject(row.staffName, row.shiftDate),
          resourceType: 'roster_assignment',
          resourceId: row.rosterAssignmentId,
          fixHref: `/payroll/readiness?period_id=${input.rosterPeriodId}&tab=assignments`,
        });
      }
      continue;
    }
    // A shift dated today or later cannot have a timesheet yet; it is still a
    // hard blocker for a snapshot, but the operator should not chase it. Today
    // is included: a shift that has not finished cannot be captured either.
    const notYetWorked = Boolean(
      row.shiftDate && input.tenantToday && row.shiftDate >= input.tenantToday,
    );
    blockers.push({
      code: 'ASSIGNMENT_NO_TIMESHEET',
      severity: 'HARD',
      message: notYetWorked
        ? 'Roster assignment has no linked timesheet yet; the shift has not been worked.'
        : 'Roster assignment has no linked timesheet.',
      subject: blockerSubject(row.staffName, row.shiftDate),
      notYetWorked: notYetWorked || undefined,
      resourceType: 'roster_assignment',
      resourceId: row.rosterAssignmentId,
      fixHref: `/payroll/readiness?period_id=${input.rosterPeriodId}&tab=assignments`,
    });
  }

  let timesheetsZeroPay = 0;
  let timesheetsUnresolvedContext = 0;
  let timesheetsPendingApproval = 0;
  let totalGrossPay = 0;

  const leaveTimesheetsByWorkerDate = new Map<string, typeof payrollRelevantTimesheets>();
  for (const timesheet of payrollRelevantTimesheets) {
    // A rejection is a recorded decision: it carries a reason, an actor and
    // worked-time evidence, and the row is never snapshotted into a pay run.
    // It therefore cannot hold the run, and the pricing checks below do not
    // apply to it (a row that will not be paid has no pay to resolve, and its
    // pay must not inflate the period gross). It stays visible as an advisory
    // so a shift rejected in error is still found and reopened before pay.
    if (String(timesheet.status ?? 'PENDING').toUpperCase() === 'REJECTED') {
      // The one rejected row that must still stop the run: it is bound to a pay
      // run, so it would be paid despite the rejection.
      const lockedToPayRun = Boolean(String(timesheet.payRunId ?? '').trim());
      blockers.push({
        code: 'TIMESHEET_REJECTED',
        severity: lockedToPayRun ? 'HARD' : 'SOFT',
        message: lockedToPayRun
          ? 'Rejected timesheet is locked to a pay run and would be paid despite the rejection. Resolve it through the pay-run-locked correction workflow before the run proceeds.'
          : 'Rejected timesheet is excluded from this pay run. If the shift was worked and should be paid, open the Rejected queue and use Reopen for correction before the run is approved.',
        subject: blockerSubject(timesheet.staffName, timesheet.date),
        resourceType: 'timesheet',
        resourceId: timesheet.id,
        fixHref: `/roster/timesheets?status=REJECTED&timesheet_id=${timesheet.id}`,
      });
      continue;
    }

    const hours = asNumber(timesheet.totalHours);
    const pay = asNumber(timesheet.totalPay);
    totalGrossPay += pay;

    const timesheetStatus = String(timesheet.status ?? 'PENDING').toUpperCase();
    if (timesheet.coveredByApprovedLeave === true && hours > 0 && timesheet.date) {
      const key = `${timesheet.staffId}|${timesheet.date}`;
      leaveTimesheetsByWorkerDate.set(key, [
        ...(leaveTimesheetsByWorkerDate.get(key) ?? []),
        timesheet,
      ]);
    }

    const status = normalisePayContextStatus(timesheet.payContextStatus);
    const snapshot = timesheet.payContextSnapshot;
    const schemaVersion = payContextSchemaVersion(snapshot);
    const sourceKind = payContextText(snapshot, 'sourceKind');

    if (!RESOLVED_PAY_CONTEXT.has(status) || schemaVersion !== 2) {
      timesheetsUnresolvedContext += 1;
      blockers.push({
        code: 'TIMESHEET_PAY_CONTEXT_NOT_RESOLVED',
        severity: 'HARD',
        message: `Timesheet pay context is ${status}${schemaVersion !== 2 ? ' (snapshot missing or not v2)' : ''}.`,
        subject: blockerSubject(timesheet.staffName, timesheet.date),
        resourceType: 'timesheet',
        resourceId: timesheet.id,
        fixHref: `/payroll/timesheet/${timesheet.id}/breakdown`,
      });
    } else if (sourceKind && sourceKind !== 'TIMESHEET') {
      timesheetsUnresolvedContext += 1;
      blockers.push({
        code: 'TIMESHEET_PAY_CONTEXT_STALE',
        severity: 'HARD',
        message: `Timesheet pay context source is ${sourceKind}; recompute to refresh TIMESHEET snapshot.`,
        subject: blockerSubject(timesheet.staffName, timesheet.date),
        resourceType: 'timesheet',
        resourceId: timesheet.id,
        fixHref: `/payroll/timesheet/${timesheet.id}/breakdown`,
      });
    }

    if (hours > 0 && pay <= 0 && !isSalaryFlatTimesheet(timesheet)) {
      timesheetsZeroPay += 1;
      blockers.push({
        code: 'TIMESHEET_ZERO_PAY',
        severity: 'HARD',
        message: `Timesheet has ${hours}h worked but $0 pay.`,
        subject: blockerSubject(timesheet.staffName, timesheet.date),
        resourceType: 'timesheet',
        resourceId: timesheet.id,
        fixHref: `/payroll/timesheet/${timesheet.id}/breakdown`,
      });
    }

    const tsStatus = timesheetStatus;
    if (tsStatus === 'PENDING' || tsStatus === 'SUBMITTED') {
      timesheetsPendingApproval += 1;
      blockers.push({
        code: 'TIMESHEET_NOT_APPROVED',
        severity: 'SOFT',
        message: 'Timesheet is pending manager approval.',
        subject: blockerSubject(timesheet.staffName, timesheet.date),
        resourceType: 'timesheet',
        resourceId: timesheet.id,
        fixHref: '/roster/timesheets',
      });
    }
  }

  // Emit this once per worker/day across every live or manual timesheet. A
  // same-day leave advisory must not disappear merely because it is unrostered
  // or because another shift on a multi-shift day was the leave's identity.
  for (const timesheets of leaveTimesheetsByWorkerDate.values()) {
    const live = timesheets.filter((timesheet) =>
      ['PENDING', 'SUBMITTED', 'APPROVED'].includes(String(timesheet.status).toUpperCase()),
    );
    if (live.length === 0) continue;
    const hardTimesheet = live.find((timesheet) => {
      const leaveHours = asNumber(timesheet.identifiedPaidLeaveHoursForAssignment);
      const windowHours = timesheet.grossScheduledWindowHoursForAssignment;
      // HARD requires leave the snapshot WILL pay in this run; public-holiday,
      // already-applied and unresolved-payability leave remain SOFT advisories.
      return (
        timesheet.identifiedPaidLeaveWillBePaidInRun === true &&
        leaveHours > 0 &&
        typeof windowHours === 'number' &&
        Number.isFinite(windowHours) &&
        leaveHours + asNumber(timesheet.totalHours) > windowHours + 0.25
      );
    });
    const representative = hardTimesheet ?? live[0]!;
    blockers.push({
      code: 'TIMESHEET_AND_APPROVED_LEAVE_SAME_DAY',
      severity: hardTimesheet ? 'HARD' : 'SOFT',
      message: hardTimesheet
        ? 'Timesheet and identified paid leave exceed this assignment’s gross scheduled window. Reject the timesheet if the leave replaced the shift, or amend the leave hours if they worked part of the day.'
        : 'This worker has a timesheet and approved leave on the same day. If the leave replaced the shift, reject the timesheet; if they worked part of the day, check the leave hours. Paying both may overpay.',
      subject: blockerSubject(representative.staffName, representative.date),
      resourceType: 'timesheet',
      resourceId: representative.id,
      fixHref: `/roster/timesheets?timesheet_id=${representative.id}`,
    });
  }

  const workersMissingPayProfile = input.workersMissingPayProfileIds?.length ?? 0;
  for (const staffId of input.workersMissingPayProfileIds ?? []) {
    blockers.push({
      code: 'WORKER_MISSING_PAY_PROFILE',
      severity: 'HARD',
      message: 'Worker has no active pay profile for this period.',
      resourceType: 'staff',
      resourceId: staffId,
      fixHref: `/people/workers/${staffId}/pay-profile`,
    });
  }

  const rateGaps =
    input.rateScheduleLines && input.rateScheduleLines.length > 0
      ? scanRateScheduleGaps(
          collectRateScheduleKeysFromSnapshots(payrollRelevantTimesheets),
          input.rateScheduleLines,
        )
      : [];
  for (const gap of rateGaps) {
    blockers.push({
      code: 'RATE_SCHEDULE_GAP',
      severity: 'HARD',
      message: `No rate line for ${gap.classificationStream} ${gap.classificationLevel}${gap.payPoint ? ` (${gap.payPoint})` : ''}.`,
      resourceType: 'rate_line',
      fixHref: '/settings/payroll/eba',
    });
  }

  const exportReadiness = input.exportReadiness ?? {
    status: 'NOT_REQUIRED',
    targetSystem: 'NONE',
    ebaId: null,
    blockers: [],
  };

  const hardBlockers = blockers.filter((b) => b.severity === 'HARD');
  const calculationStatus: PayrollCalculationStatus =
    staffedAssignments.length === 0 && payrollRelevantTimesheets.length === 0
      ? 'UNKNOWN'
      : hardBlockers.length === 0
        ? 'READY'
        : 'BLOCKED';

  return {
    rosterPeriodId: input.rosterPeriodId,
    periodStart: input.periodStart ?? null,
    periodEnd: input.periodEnd ?? null,
    calculation: {
      status: calculationStatus,
      blockers: dedupeBlockers(blockers),
      metrics: {
        assignmentCount: staffedAssignments.length,
        assignmentsResolved,
        assignmentsBlocked,
        assignmentsWithoutTimesheet,
        timesheetCount: payrollRelevantTimesheets.length,
        timesheetsZeroPay,
        timesheetsUnresolvedContext,
        timesheetsPendingApproval,
        workersMissingPayProfile,
        rateScheduleGaps: rateGaps.length,
        totalGrossPay: round2(totalGrossPay),
      },
    },
    export: exportReadiness,
    leavePeriodEvidence: input.leavePeriodEvidence ?? null,
    timesheetCorrectionsEvidence: input.timesheetCorrectionsEvidence ?? null,
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function dedupeBlockers(blockers: PeriodReadinessBlocker[]): PeriodReadinessBlocker[] {
  const seen = new Set<string>();
  const result: PeriodReadinessBlocker[] = [];
  for (const blocker of blockers) {
    const key = [
      blocker.code,
      blocker.resourceType ?? '',
      blocker.resourceId ?? '',
      blocker.message,
    ].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(blocker);
  }
  return result;
}

export function isCalculationReady(summary: PayrollPeriodReadinessSummary): boolean {
  return summary.calculation.status === 'READY';
}

/**
 * A blocker that names a source (timesheet or leave request) actively held
 * out of the draft pay run. Such an item is never written, so the blocker
 * cannot refuse the snapshot and should not count on the readiness page.
 * Only source-scoped blockers qualify: an emitter that types its resource as
 * staff, roster period or correction keeps refusing even if its id were ever
 * to coincide with a held-out source id.
 *
 * 22 Sep 2026, Medea 7–20 Sep: five leave items were held out from the
 * rehearsal, the rehearsal was clear, and both the live snapshot gate and the
 * readiness banner still counted their LEAVE_ROSTER_EVIDENCE_GAP rows.
 */
export function isHeldOutSourceBlocker(
  blocker: Pick<PeriodReadinessBlocker, 'resourceId' | 'resourceType'>,
  heldOutSourceIds: ReadonlySet<string>,
): boolean {
  return (
    blocker.resourceId != null &&
    heldOutSourceIds.has(blocker.resourceId) &&
    (blocker.resourceType == null ||
      blocker.resourceType === 'timesheet' ||
      blocker.resourceType === 'leave_request')
  );
}

/**
 * Returns the summary with held-out source blockers removed and the
 * calculation status re-derived from what remains. Metrics remain period-wide;
 * the source count lets consumers explain why those counts can differ from the
 * active-run blockers. UNKNOWN stays UNKNOWN.
 */
export function discountHeldOutReadinessBlockers(
  summary: PayrollPeriodReadinessSummary,
  heldOutSourceIds: ReadonlySet<string>,
): PayrollPeriodReadinessSummary {
  if (heldOutSourceIds.size === 0) return summary;
  const blockers = summary.calculation.blockers.filter(
    (blocker) => !isHeldOutSourceBlocker(blocker, heldOutSourceIds),
  );
  const status: PayrollCalculationStatus =
    summary.calculation.status === 'UNKNOWN'
      ? 'UNKNOWN'
      : blockers.length === summary.calculation.blockers.length
        ? summary.calculation.status
        : blockers.some((blocker) => blocker.severity === 'HARD')
          ? 'BLOCKED'
          : 'READY';
  return {
    ...summary,
    calculation: {
      ...summary.calculation,
      status,
      blockers,
      heldOutSourceCount: heldOutSourceIds.size,
    },
  };
}

export function isExportReady(summary: PayrollPeriodReadinessSummary): boolean {
  return summary.export.status === 'READY' || summary.export.status === 'NOT_REQUIRED';
}
